// The app's own page scroll: slower and softer than the browser's smooth
// scroll, so the view drifts after the cards rather than jumping to them.

let current = 0;

// A reader who scrolls, swipes or presses a key mid-glide takes over: the
// page must never pull them back to where it had meant to go.
const INTERRUPTS = ['wheel', 'touchstart', 'keydown'];

const listen = (on) => INTERRUPTS.forEach(type => (on
  ? window.addEventListener(type, cancelGlide, { passive: true })
  : window.removeEventListener(type, cancelGlide)));

function cancelGlide() {
  cancelAnimationFrame(current);
  listen(false);
}

// The page sets `scroll-behavior: smooth`, which would turn every frame's
// scrollTo into a smooth scroll of its own; the glide drives it frame by
// frame, so each step must land at once.
const jump = (top) => window.scrollTo({ top, behavior: 'instant' });

const easeInOut = (p) => (p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2);

/** Where content should land: just below the sticky dash header. */
const headerClearance = () =>
  (document.querySelector('.dash-header')?.offsetHeight || 0) + 16;

/**
 * Scrolls the window to `top` over `duration` ms. A newer glide cancels an
 * older one, and reduced motion jumps straight there.
 */
export const glideTo = (top, duration = 1000) => {
  cancelGlide();
  const max = document.documentElement.scrollHeight - window.innerHeight;
  const target = Math.max(0, Math.min(top, max));
  const start = window.pageYOffset;
  const distance = target - start;

  if (Math.abs(distance) < 2) return;
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
    jump(target);
    return;
  }

  let startTime = null;
  const step = (now) => {
    if (startTime === null) startTime = now;
    const progress = Math.min((now - startTime) / duration, 1);
    jump(start + distance * easeInOut(progress));
    if (progress < 1) current = requestAnimationFrame(step);
    else listen(false);
  };
  current = requestAnimationFrame(step);
  listen(true);
};

/** Glides until viewport position `top` sits just below the header. */
export const glideToTop = (top, { duration } = {}) =>
  glideTo(window.pageYOffset + top - headerClearance(), duration);

/** Whether a box this tall fits between the header and `reserve` px above the bottom. */
export const fitsInView = (height, reserve = 24) =>
  height <= window.innerHeight - reserve - headerClearance();

/**
 * How far to scroll to bring the box from `top` to `bottom` (viewport
 * pixels) into view between the header and `reserve` px above the bottom
 * edge. If it cannot fit, its top is put just below the header.
 */
const deltaIntoView = (top, bottom, reserve) => {
  const clearance = headerClearance();
  const room = window.innerHeight - reserve - clearance;
  if (bottom - top > room || top < clearance) return top - clearance;
  if (bottom > window.innerHeight - reserve) return bottom - (window.innerHeight - reserve);
  return 0;
};

/** Glides just far enough to bring a box into view (see deltaIntoView). */
export const glideIntoView = (top, bottom, { reserve = 24, duration } = {}) => {
  const delta = deltaIntoView(top, bottom, reserve);
  if (delta) glideTo(window.pageYOffset + delta, duration);
};
